import { useId, useState } from 'react';
import { analytics, isAnalyticsOptedOut, setAnalyticsOptOut } from './client';
import './AnalyticsToggle.css';

/**
 * Rail opt-out control for first-party usage analytics (cue-forge-do7.3). A
 * single checkbox: checked means analytics is ON (the user has NOT opted out).
 *
 * Rendered only when an ingest endpoint is configured (`analytics.enabled`). In
 * dev/CI/e2e without `VITE_ANALYTICS_URL` the whole client is inert, so the
 * toggle would be a no-op control — and its setter still writes the opt-out key
 * to storage, so exposing it in an unconfigured build would let a dev persist a
 * consent choice against a disabled feature. Hidden is simplest and honest.
 *
 * The consent helpers are injectable so the seam is testable against a live
 * `createAnalytics` instance; they default to the real singleton in the app.
 */
interface AnalyticsToggleProps {
  enabled?: boolean;
  isOptedOut?: () => boolean;
  setOptOut?: (v: boolean) => void;
}

export function AnalyticsToggle({
  enabled = analytics.enabled,
  isOptedOut = isAnalyticsOptedOut,
  setOptOut = setAnalyticsOptOut,
}: AnalyticsToggleProps = {}) {
  // Seed from the persisted opt-out so the checkbox reflects the current choice.
  const [optedOut, setOptedOut] = useState(() => isOptedOut());
  const hintId = useId();

  if (!enabled) return null;

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const on = e.target.checked; // checked = analytics ON = not opted out
    // Call the setter (not raw storage): the singleton caches nothing, but
    // opting out must also drop the anon id + pending queue immediately.
    setOptOut(!on);
    setOptedOut(!on);
  };

  return (
    <section className="cf-analytics" aria-label="Usage analytics">
      <label className="cf-analytics__row">
        <input
          type="checkbox"
          className="cf-analytics__input"
          checked={!optedOut}
          onChange={onChange}
          aria-describedby={hintId}
        />
        <span className="cf-analytics__label">Usage analytics</span>
      </label>
      <p id={hintId} className="cf-analytics__hint">
        Anonymous feature-usage counts. No files, no text.
      </p>
    </section>
  );
}
